import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    Check,
    X,
    Sparkles,
    Award,
    Calendar,
    Home,
    TrendingUp,
    BarChart3,
    Search,
    Loader2,
    ArrowRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, token } = useAuth();
    const [plans, setPlans] = useState([]);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processingPlan, setProcessingPlan] = useState(null);

    const wasCanceled = searchParams.get('canceled') === 'true';

    useEffect(() => {
        fetchPlans();
        if (user?.user_type === 'agent' && token) {
            fetchCurrentPlan();
        }
    }, [user, token]);

    useEffect(() => {
        if (wasCanceled) {
            toast.info('Paiement annulé. Vous pouvez réessayer quand vous voulez.');
        }
    }, [wasCanceled]);

    const fetchPlans = async () => {
        try {
            const response = await axios.get(`${API}/subscriptions/plans`);
            setPlans(response.data);
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentPlan = async () => {
        try {
            const response = await axios.get(`${API}/subscriptions/my`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCurrentPlan(response.data);
        } catch (error) {
            console.error('Error fetching current plan:', error);
        }
    };

    const handleSelectPlan = async (planId) => {
        if (!user) {
            toast.info('Connectez-vous pour souscrire à un abonnement');
            navigate('/register?type=agent');
            return;
        }

        if (user.user_type !== 'agent') {
            toast.error('Seuls les agents peuvent souscrire à un abonnement');
            return;
        }

        if (currentPlan?.plan_id === planId) {
            toast.info('Vous avez déjà cet abonnement');
            return;
        }

        setProcessingPlan(planId);

        try {
            const response = await axios.post(
                `${API}/subscriptions/checkout`,
                {
                    plan_id: planId,
                    origin_url: window.location.origin
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.checkout_url) {
                // Redirect to Stripe checkout
                window.location.href = response.data.checkout_url;
            } else if (response.data.redirect_url) {
                // Free plan - redirect to dashboard
                toast.success('Plan Starter activé !');
                navigate('/dashboard');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la souscription');
        } finally {
            setProcessingPlan(null);
        }
    };

    const planIcons = {
        starter: <Calendar className="w-6 h-6" />,
        pro: <Home className="w-6 h-6" />,
        elite: <Sparkles className="w-6 h-6" />,
    };

    const featureLabels = {
        max_appointments_per_month: (v) => v === -1 ? 'RDV illimités' : `${v} RDV/mois`,
        top_3_properties: (v) => v ? 'Top 3 des biens' : null,
        verified_badge: (v) => v ? 'Badge vérifié' : null,
        visibility_boost: (v) => v > 0 ? `+${v} boost visibilité` : null,
        advanced_stats: (v) => v ? 'Statistiques avancées' : null,
        priority_search: (v) => v ? 'Priorité dans les recherches' : null,
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-scalimmo-navy" />
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-24 pb-16" data-testid="pricing-page">
            {/* Header */}
            <div className="text-center mb-16">
                <Badge className="mb-4 bg-scalimmo-gold/10 text-scalimmo-gold border-scalimmo-gold/30">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Tarifs agents
                </Badge>
                <h1 className="font-heading text-4xl md:text-5xl font-semibold text-scalimmo-navy mb-4">
                    Développez votre activité
                </h1>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    Choisissez l'offre adaptée à vos besoins et boostez votre visibilité auprès des vendeurs.
                </p>
            </div>

            {/* Pricing Cards */}
            <div className="container-scalimmo">
                <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                    {plans.map((plan) => {
                        const isCurrentPlan = currentPlan?.plan_id === plan.id;
                        const isPopular = plan.id === 'pro';
                        const isElite = plan.id === 'elite';

                        return (
                            <div
                                key={plan.id}
                                className={`relative bg-white rounded-2xl p-8 ${
                                    isElite
                                        ? 'ring-2 ring-scalimmo-gold shadow-lg'
                                        : isPopular
                                        ? 'ring-2 ring-scalimmo-navy shadow-lg'
                                        : 'border border-border shadow-soft'
                                }`}
                                data-testid={`plan-${plan.id}`}
                            >
                                {/* Popular Badge */}
                                {isPopular && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <Badge className="bg-scalimmo-navy text-white px-4 py-1">
                                            Populaire
                                        </Badge>
                                    </div>
                                )}
                                {isElite && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <Badge className="elite-badge px-4 py-1">
                                            <Award className="w-3 h-3 mr-1" />
                                            Premium
                                        </Badge>
                                    </div>
                                )}

                                {/* Plan Header */}
                                <div className="text-center mb-6">
                                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 ${
                                        isElite
                                            ? 'bg-scalimmo-gold/10 text-scalimmo-gold'
                                            : 'bg-scalimmo-navy/5 text-scalimmo-navy'
                                    }`}>
                                        {planIcons[plan.id]}
                                    </div>
                                    <h2 className="font-heading text-2xl font-semibold text-scalimmo-navy">
                                        {plan.name}
                                    </h2>
                                    <div className="mt-4">
                                        <span className="text-4xl font-heading font-bold text-scalimmo-navy">
                                            {plan.price === 0 ? 'Gratuit' : `${plan.price}€`}
                                        </span>
                                        {plan.price > 0 && (
                                            <span className="text-muted-foreground">/mois</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-2">
                                        {plan.description}
                                    </p>
                                </div>

                                {/* Features List */}
                                <ul className="space-y-3 mb-8">
                                    {Object.entries(plan.features).map(([key, value]) => {
                                        const label = featureLabels[key]?.(value);
                                        if (!label) return null;

                                        return (
                                            <li key={key} className="flex items-center gap-3">
                                                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                                    isElite
                                                        ? 'bg-scalimmo-gold/20 text-scalimmo-gold'
                                                        : 'bg-green-100 text-green-600'
                                                }`}>
                                                    <Check className="w-3 h-3" />
                                                </div>
                                                <span className="text-sm text-scalimmo-navy">{label}</span>
                                            </li>
                                        );
                                    })}

                                    {/* Show what's NOT included for starter */}
                                    {plan.id === 'starter' && (
                                        <>
                                            <li className="flex items-center gap-3 opacity-50">
                                                <div className="w-5 h-5 rounded-full flex items-center justify-center bg-gray-100 text-gray-400">
                                                    <X className="w-3 h-3" />
                                                </div>
                                                <span className="text-sm text-muted-foreground">Top 3 des biens</span>
                                            </li>
                                            <li className="flex items-center gap-3 opacity-50">
                                                <div className="w-5 h-5 rounded-full flex items-center justify-center bg-gray-100 text-gray-400">
                                                    <X className="w-3 h-3" />
                                                </div>
                                                <span className="text-sm text-muted-foreground">Badge vérifié</span>
                                            </li>
                                        </>
                                    )}
                                </ul>

                                {/* CTA Button */}
                                <Button
                                    className={`w-full ${
                                        isCurrentPlan
                                            ? 'bg-green-600 hover:bg-green-700'
                                            : isElite
                                            ? 'btn-gold'
                                            : isPopular
                                            ? 'btn-primary'
                                            : 'btn-secondary'
                                    }`}
                                    onClick={() => handleSelectPlan(plan.id)}
                                    disabled={isCurrentPlan || processingPlan === plan.id}
                                    data-testid={`select-${plan.id}`}
                                >
                                    {processingPlan === plan.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : isCurrentPlan ? (
                                        <>
                                            <Check className="w-4 h-4 mr-2" />
                                            Plan actuel
                                        </>
                                    ) : (
                                        <>
                                            {plan.price === 0 ? 'Commencer gratuitement' : 'Choisir ce plan'}
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        );
                    })}
                </div>

                {/* FAQ Section */}
                <div className="mt-20 max-w-3xl mx-auto">
                    <h2 className="font-heading text-2xl font-semibold text-scalimmo-navy text-center mb-8">
                        Questions fréquentes
                    </h2>
                    <div className="space-y-4">
                        <div className="bg-white rounded-xl p-6 border border-border/60">
                            <h3 className="font-medium text-scalimmo-navy mb-2">
                                Puis-je changer de plan à tout moment ?
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Oui, vous pouvez passer à un plan supérieur à tout moment. 
                                Le changement prend effet immédiatement.
                            </p>
                        </div>
                        <div className="bg-white rounded-xl p-6 border border-border/60">
                            <h3 className="font-medium text-scalimmo-navy mb-2">
                                Comment fonctionne le badge vérifié ?
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Le badge vérifié apparaît sur votre profil et vos annonces pour 
                                rassurer les vendeurs sur votre professionnalisme.
                            </p>
                        </div>
                        <div className="bg-white rounded-xl p-6 border border-border/60">
                            <h3 className="font-medium text-scalimmo-navy mb-2">
                                Qu'est-ce que le boost de visibilité ?
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Le boost de visibilité améliore votre position dans les résultats de recherche. 
                                Plus votre boost est élevé, plus vous apparaissez en haut de la liste.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingPage;
